import moment from "moment";
import { DateFilterTypes } from "constants/";

/**
 * This file can contain common helper functions that can
 * be import from here and used across the app
 */

function generateUUID() {
  function randomDigit() {
    const crypto = window.crypto || window.msCrypto;
    if (crypto && crypto.getRandomValues) {
      const rands = new Uint8Array(1);
      crypto.getRandomValues(rands);
      return (rands[0] % 16).toString(16);
    }
    // eslint-disable-next-line no-bitwise
    return ((Math.random() * 16) | 0).toString(16);
  }

  return "xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx".replace(/x/g, randomDigit);
}

/**
 * Will convert an array of objects into an object with
 * desired value as the key, can optianlly add or delete
 * values from original object as well while converting.
 */
function getObjectByKeys(arr, key = "id", deleteKey = null, addKeys = null) {
  const obj = {};
  arr.forEach((val) => {
    obj[val[key]] = val;
    if (deleteKey) {
      delete obj[val[key]][deleteKey];
    }
    if (addKeys) {
      obj[val[key]] = {
        ...obj[val[key]],
        ...addKeys,
      };
    }
  });
  return obj;
}

/**
 * Will convert an array of objects into an object with
 * desired value as key. If several keys have same value
 * then it will group them into an array.
 */
function groupByKeys(data, key = "id") {
  const obj = {};
  data.forEach((item) => {
    if (!obj[item[key]]) {
      obj[item[key]] = [];
    }
    obj[item[key]].push(item);
  });
  return obj;
}

/**
 * we can use Promise.allSettled as well but
 * due to less browser support added custom one.
 */
const promiseAllSettled = (promises) =>
  Promise.all(
    promises.map((p) =>
      p
        .then((value) => ({
          status: "fulfilled",
          value,
        }))
        .catch((reason) => ({
          status: "rejected",
          reason,
        })),
    ),
  );

function formatNumber(number, minimumFractionDigits = 0, currency = null) {
  if (!isNaN(number)) {
    const locale = navigator.language || "en-US";
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: number % 1 === 0 ? 0 : minimumFractionDigits, // if number is integer then no decimal
      ...(currency && { currency }),
    }).format(number);
  }
  return number;
}

function formatNumberV2(number, { minimumFractionDigits = 0, maximumFractionDigits = 2 } = {}) {
  if (!isNaN(number)) {
    const locale = navigator.language || "en-US";
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(number);
  }
  return number;
}

function formatPrice(number, currency, minimumFractionDigits = 2) {
  if (!isNaN(number)) {
    const locale = navigator.language || "en-US";
    return `${new Intl.NumberFormat(locale, {
      minimumFractionDigits,
    }).format(number)} ${currency}`;
  }
  return `${number} ${currency}`;
}

function formatPriceV2(number, { currency = "", minimumFractionDigits = 2, maximumFractionDigits = 2 } = {}) {
  if (!isNaN(number)) {
    const locale = navigator.language || "en-US";
    return `${new Intl.NumberFormat(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(number)} ${currency}`;
  }
  return `${number} ${currency}`;
}

function roundUp(num, { decimalPlaces = 2 } = {}) {
  const factor = 10 ** decimalPlaces;
  return (Math.ceil(num * factor) / factor).toFixed(decimalPlaces);
}

function getDecimalCount(number) {
  if (typeof number === "number" && !isNaN(number) && !Number.isInteger(number)) {
    return number.toString().split(".")[1].length;
  }
  return 0;
}

function getFirstChar(text) {
  if (!text || typeof text !== "string") {
    return "";
  }
  return text.charAt(0).toUpperCase();
}

/**
 * Returns a boolean determining if the current host is localhost
 */
const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === "[::1]" ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/),
);

function replaceLinebreaks(message) {
  if (typeof message === "string" && message?.length) {
    return message.replace(/[↵\n]/g, "<br/>");
  }
  return message;
}

function getDateParams(data, key) {
  if (data?.type === DateFilterTypes.CUSTOM && data.startDate && data.endDate) {
    return {
      [`${key}_start`]: moment(data.startDate).format("YYYY-MM-DD"),
      [`${key}_end`]: moment(data.endDate).format("YYYY-MM-DD"),
    };
  }
  if (data?.type !== DateFilterTypes.CUSTOM) {
    return {
      [`${key}_type`]: data.type,
    };
  }
  return {};
}

function appendDefaultOption(options, intl) {
  return [
    {
      label: intl.formatMessage({
        id: "general.pleaseSelect",
        defaultMessage: "Please select",
      }),
      value: "",
    },
    ...(options ?? []),
  ];
}

/**
 * filter function for react-bootstrap-typehead incase you
 * need it to show all options when an option is selected.
 */
function filterWhenNonSelected(option, itemProps, filterKey = "label") {
  if (itemProps.selected.length) {
    return true;
  }
  return option[filterKey].toLowerCase().indexOf(itemProps.text.toLowerCase()) !== -1;
}

/**
 * Returns a rounded number to the specified decimal places
 * @param {*} number - number to round
 * @param {*} precision - number of decimal places to round to
 * @returns rounded number
 */
function roundTo(number, precision) {
  return +`${Math.round(`${number}e+${precision}`)}e-${precision}`;
}

/**
 * A helper function to format a datetime, helps us to maintain consistency in datetime formats
 * @param {*} datetime the datetime to format
 * @param {*} customFormat a custom format to use, defaults to "DD MMM HH:mm"
 * @returns
 */
function formatDatetime(datetime, customFormat = "DD MMM HH:mm") {
  if (datetime === null || datetime === " ") return "-";
  const dtMoment = moment(datetime);
  if (!dtMoment.isValid()) return datetime;
  return dtMoment.format(customFormat);
}

/**
 * A helper function to format a weight
 * @param {*} datetime the weight to format
 * @param {*} unit the weight measurement unit to be appended
 * @returns string
 */
function formatWeight(weight, unit = "") {
  if (!weight || weight === null || weight === "") return "-";
  const locale = navigator.language || "en-US";
  const amount = new Intl.NumberFormat(locale, {
    minimumFractionDigits: weight % 1 === 0 ? 0 : 2,
  }).format(weight);

  return `${amount} ${unit}`;
}

/**
 * A helper function to see if the selected date is a weekday
 * @param {*} date the datetime to format
 * @returns a boolean
 */
function isWeekday(date) {
  const dayOfWeek = moment(date).day();
  return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday = 1, Friday = 5
}

/**
 * Capitalize the first letter of a string
 * @param {*} str the string to capitalize
 * @returns a string with the first letter capitalized
 */
function capitalizeFirstLetter(str) {
  if (!str) return "";
  // Capitalize the first letter and concatenate the rest of the string
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function pollFn({ fn, delay, maxPollCount, onComplete, callFirstTimeImmediately = false }) {
  let pollCount = 0;
  if (!maxPollCount) {
    return;
  }
  if (callFirstTimeImmediately) {
    fn();
  }
  const intervalRef = setInterval(() => {
    if (pollCount >= maxPollCount) {
      clearInterval(intervalRef);
      if (onComplete) {
        onComplete();
      }
      return;
    }
    fn();
    pollCount += 1;
  }, delay);
  return intervalRef;
}

/**
 * Take only two decimal places of a number WITHOUT ROUNDING
 * @param {*} number the number to format
 * @returns formatted number with two decimal places
 */
function sliceDecimalsWithoutRounding(number, { returnAsNumber = true } = {}) {
  const output = number.toString().match(/^-?\d+(\.\d{0,2})?/)[0];
  if (returnAsNumber) return Number(output);
  return output;
}

/**
 * A helper function to conditionally join classnames
 * @param  {...(string | object)} classes - The classes to join, can be strings or objects
 * @returns {string} - A string with the joined classes
 * @example classNames("class1", "class2", { "expanded": isExpanded, "mt-10": index === 0 })
 */
function classNames(...classes) {
  return classes
    .flatMap((cls) => {
      if (typeof cls === "string") {
        return cls;
      }
      if (typeof cls === "object" && cls !== null) {
        return Object.keys(cls).filter((key) => cls[key]);
      }
      return [];
    })
    .join(" ");
}

function prettifyBytes(bytes) {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  if (bytes === 0) return "0 Bytes";

  const i = Math.floor(Math.log(bytes) / Math.log(1000));
  const size = sizes[i];

  const value = (bytes / 1000 ** i).toFixed(2);

  return `${value} ${size}`;
}

/**
 * Recursively checks whether two values are deeply equal.
 * This function handles primitives, arrays, plain objects, and Date objects.
 * It does not handle circular references.
 * @param {*} a - The first value to compare.
 * @param {*} b - The second value to compare.
 * @returns {boolean} - Returns true if both values are deeply equal, otherwise false.
 */
function isEqual(a, b) {
  if (a === b) return true;

  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") {
    return false;
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  const isArrayA = Array.isArray(a);
  const isArrayB = Array.isArray(b);
  if (isArrayA !== isArrayB) return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) => Object.prototype.hasOwnProperty.call(b, key) && isEqual(a[key], b[key]));
}

/**
 * Decodes HTML entities from a given string.
 * This function converts encoded HTML characters (e.g., `&lt;` to `<`, `&gt;` to `>`)
 * using the browser's built-in DOMParser.
 *
 * @param {string} input - The HTML-encoded string to decode.
 * @returns {string} - The decoded string with entities replaced by their corresponding characters.
 */
function decodeHTML(input) {
  return new DOMParser().parseFromString(input, "text/html").body.textContent;
}

function convertWeight(weight, { fromUnit, toUnit, decimalPlaces = 2 }) {
  if (fromUnit === toUnit) {
    return weight.toFixed(decimalPlaces);
  }

  const conversionRates = {
    kg: 1,
    lbs: 2.20462,
  };

  if (!conversionRates[fromUnit] || !conversionRates[toUnit]) {
    throw new Error("Invalid weight unit provided");
  }

  const parsedWeight = parseFloat(weight);
  const weightInKg = parsedWeight / conversionRates[fromUnit];
  const convertedWeight = weightInKg * conversionRates[toUnit];
  return convertedWeight.toFixed(decimalPlaces);
}

const getFirstAvailable = (...fields) => fields.find((f) => f) || "";

const formatFileSize = (bytes) => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const objectToText = (obj) =>
  Object.entries(obj)
    .map(([key, value]) => `${key}: ${value}`)
    .join(", ");

export {
  capitalizeFirstLetter,
  classNames,
  generateUUID,
  getObjectByKeys,
  groupByKeys,
  promiseAllSettled,
  decodeHTML,
  isLocalhost,
  formatNumber,
  formatNumberV2,
  formatPrice,
  formatPriceV2,
  getDecimalCount,
  getFirstChar,
  isEqual,
  replaceLinebreaks,
  getDateParams,
  appendDefaultOption,
  filterWhenNonSelected,
  roundTo,
  formatDatetime,
  formatWeight,
  isWeekday,
  pollFn,
  sliceDecimalsWithoutRounding,
  roundUp,
  prettifyBytes,
  convertWeight,
  getFirstAvailable,
  formatFileSize,
  objectToText,
};
